/**
 * The parts of the two password steps — the first-sign-in challenge and a
 * forgotten-password reset — that are worth testing on their own: what goes
 * back to Cognito in the challenge response, what we refuse before spending a
 * round trip, and how a Cognito error is worded for someone standing at a bar
 * with no admin to call.
 *
 * Kept free of React and of `amazon-cognito-identity-js` so it can be unit
 * tested — the exchanges themselves cannot be, without a live pool.
 */

/**
 * The library hands `requiredAttributes` back with Cognito's
 * `userAttributes.` prefix already stripped, but the raw challenge parameters
 * carry it and older versions passed it through. Strip it defensively: a name
 * that still has the prefix would be sent as `userAttributes.userAttributes.x`
 * and rejected.
 */
const PREFIX = "userAttributes.";

/**
 * Attributes Cognito maintains itself and refuses in a challenge response.
 * `email_verified` is the one that bites: `AdminCreateUser` sets it (see
 * `inviteToPool`), so it comes back in `userAttributes`, and handing it
 * straight back fails the challenge with a parameter error.
 */
const NON_WRITABLE = new Set([
  "sub",
  "identities",
  "username",
  "user_status",
  "mfa_setting",
  "preferred_mfa_setting",
]);

/** False for anything Cognito will not accept in a challenge response. */
export function isWritableAttribute(name: string): boolean {
  if (!name) return false;
  // `cognito:username`, `cognito:groups`, `cognito:user_status` — all read-only.
  if (name.startsWith("cognito:")) return false;
  // `email_verified`, `phone_number_verified`: set by the pool, never by us.
  if (name.endsWith("_verified")) return false;
  return !NON_WRITABLE.has(name);
}

export interface ChallengeAttributes {
  /** What to pass as `requiredAttributeData`. */
  readonly attributes: Record<string, string>;
  /** Required attributes the challenge gave us no value for. */
  readonly missing: readonly string[];
}

function asRecord(value: unknown): Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function attributeName(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const name = raw.startsWith(PREFIX) ? raw.slice(PREFIX.length) : raw;
  return name.trim() === "" ? null : name;
}

/**
 * What to send back with the new password.
 *
 * An allow-list, not a deny-list: only the attributes Cognito asked for go
 * back, filled from the ones it handed us, and anything non-writable is
 * dropped even if it was asked for. Everything else in `userAttributes` —
 * `sub`, `email_verified`, `email` on a pool that aliases it — stays behind.
 * Sending back the whole object is the classic way to fail this challenge.
 */
export function challengeAttributes(
  userAttributes: unknown,
  requiredAttributes: unknown,
): ChallengeAttributes {
  const have = asRecord(userAttributes);
  const wanted = Array.isArray(requiredAttributes) ? requiredAttributes : [];

  const attributes: Record<string, string> = {};
  const missing: string[] = [];

  for (const raw of wanted) {
    const name = attributeName(raw);
    if (!name || !isWritableAttribute(name)) continue;
    const value = have[name];
    if (typeof value === "string" && value !== "") attributes[name] = value;
    else if (!missing.includes(name)) missing.push(name);
  }

  return { attributes, missing };
}

/** Drop anything the person typed that Cognito will not take. */
export function writableOnly(extra: Record<string, string>): Record<string, string> {
  return Object.fromEntries(
    Object.entries(extra).filter(([name, value]) => isWritableAttribute(name) && value !== ""),
  );
}

/** `given_name` → `Given name`. For labelling an attribute we did not expect. */
export function humaniseAttribute(name: string): string {
  const words = name.replace(/^custom:/, "").replace(/[_.]+/g, " ").trim();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : name;
}

/** The pool is on Cognito's default policy — no policy is set in `sst.config.ts`. */
export const MIN_PASSWORD_LENGTH = 8;

export const PASSWORD_HINT =
  "At least 8 characters, with an upper-case letter, a lower-case letter, a number and a symbol.";

/**
 * The checks worth making before a round trip. Deliberately short of the full
 * policy: the pool owns that, and guessing it here would reject a password
 * Cognito would have taken. What this must catch is the typo — a mistyped
 * password on a first sign-in burns the temporary one and needs an admin.
 */
export function passwordProblem(password: string, confirm: string): string | null {
  if (!password) return "Choose a password.";
  if (password.length < MIN_PASSWORD_LENGTH) {
    return `Use at least ${MIN_PASSWORD_LENGTH} characters. ${PASSWORD_HINT}`;
  }
  if (password !== confirm) return "The two passwords do not match.";
  return null;
}

/** A pasted code arrives with a space or a newline around it. */
export function normaliseCode(code: string): string {
  return code.replace(/\s+/g, "");
}

/**
 * The checks worth making before spending the code.
 *
 * The length of the code is deliberately not checked. Cognito emails six
 * digits today, but the length is the pool's to decide and a guess here would
 * refuse a code that would have worked — the same reasoning as
 * `passwordProblem` not re-implementing the password policy.
 */
export function resetProblem(
  code: string,
  password: string,
  confirm: string,
): string | null {
  if (!normaliseCode(code)) return "Enter the code from the email.";
  return passwordProblem(password, confirm);
}

/**
 * What the reset screen says once a code has been asked for.
 *
 * Cognito masks the address it sent to (`h***@a***.com`), which is worth
 * showing to someone who has a work address and a personal one. A null
 * destination means we are deliberately not saying whether the account
 * exists — see `requestPasswordReset`.
 */
export function describeCodeDestination(destination: string | null): string {
  return destination
    ? `A code is on its way to ${destination}.`
    : "If that address has an account, a code is on its way to it.";
}

function errorName(err: unknown): string {
  if (typeof err !== "object" || err === null) return "";
  const named = err as { name?: unknown; code?: unknown };
  if (typeof named.name === "string" && named.name !== "Error") return named.name;
  return typeof named.code === "string" ? named.code : "";
}

function errorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === "object" && err !== null) {
    const m = (err as { message?: unknown }).message;
    if (typeof m === "string") return m;
  }
  return typeof err === "string" ? err : "";
}

/** `Password did not conform with policy: Password not long enough` → the tail. */
function policyDetail(message: string): string {
  const tail = message.split(/policy:\s*/i)[1] ?? message;
  const trimmed = tail.trim();
  if (!trimmed) return "";
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

export type AuthPhase =
  | "sign-in"
  | "new-password"
  | "reset-request"
  | "reset-confirm"
  /** The code after the password, from an authenticator app (#92). */
  | "second-step"
  | "mfa-setup";

/** True for the two steps of a forgotten-password reset. */
function isReset(phase: AuthPhase): boolean {
  return phase === "reset-request" || phase === "reset-confirm";
}

/**
 * The same wording for a code that is wrong and for a code against an address
 * with no account.
 *
 * The second case should not reach here — `preventUserExistenceErrors` is on
 * and `requestPasswordReset` masks it as well — but if it ever does, it must
 * not be the one message that confirms an address is registered.
 */
const BAD_CODE =
  "That code was not accepted. Check the most recent email — asking for a new code replaces the old one.";

/**
 * An account that has never been signed into cannot be reset: Cognito holds it
 * in `FORCE_CHANGE_PASSWORD` and the temporary password is the only way in.
 * Worded without confirming that the address has an account.
 */
const NEVER_SIGNED_IN =
  "An account that has never been signed into cannot be reset this way. Sign in with the temporary password from the invitation email instead, and ask an admin to invite you again if it has expired.";

/**
 * A Cognito failure in words the person can act on. Falls back to the service
 * message rather than swallowing it — an unmapped exception the user can read
 * out beats "Something went wrong".
 */
export function describeAuthError(err: unknown, phase: AuthPhase = "sign-in"): string {
  const name = errorName(err);
  const message = errorMessage(err);

  switch (name) {
    case "InvalidPasswordException":
      return `That password does not meet this pool's rules. ${policyDetail(message)} ${PASSWORD_HINT}`.trim();
    case "InvalidParameterException":
      // "Cannot reset password for the user as there is no registered/verified
      // email or phone_number" — which contains the word password, so this has
      // to be tested before the policy branch below or it reads as a refused
      // password. Every account `inviteToPool` creates has a verified address,
      // so this is a user made by hand in the console.
      if (/no registered|verified email/i.test(message)) {
        return "This account has no verified email address, so a code cannot be sent to it. Ask an admin to reset the password for you.";
      }
      if (/password/i.test(message)) {
        return `That password was refused. ${policyDetail(message)} ${PASSWORD_HINT}`.trim();
      }
      return message || "Cognito refused the request.";
    case "NotAuthorizedException":
      if (/current state/i.test(message)) return NEVER_SIGNED_IN;
      if (/temporary password/i.test(message)) {
        return "The temporary password has expired. Ask an admin to invite you again.";
      }
      if (phase === "new-password") {
        return "This sign-in attempt has expired. Sign in with the temporary password again and set the new one straight away.";
      }
      if (isReset(phase)) {
        return "That reset could not be completed. Ask for a new code and try again.";
      }
      if (phase === "second-step") {
        return "This sign-in has gone stale. Start again with your password.";
      }
      if (phase === "mfa-setup") {
        return "Your sign-in has expired. Sign out and back in, then set it up again.";
      }
      return "Email or password not recognised.";
    case "UserNotFoundException":
      return isReset(phase) ? BAD_CODE : "Email or password not recognised.";
    case "UserNotConfirmedException":
      return "This account is not confirmed yet. Ask an admin to confirm it.";
    case "PasswordResetRequiredException":
      // Cognito puts an account here when an admin resets it. Self-service is
      // the way out of it now, so this says which button to press rather than
      // sending the person back to the admin who just pressed one.
      return "This account needs a new password. Use “Forgotten your password?” to set one.";
    case "LimitExceededException":
    case "TooManyRequestsException":
    case "TooManyFailedAttemptsException":
      return "Too many attempts. Wait a minute and try again.";
    case "CodeMismatchException":
    case "EnableSoftwareTokenMFAException":
      if (phase === "second-step" || phase === "mfa-setup") {
        // An authenticator code is only good for thirty seconds, and a phone
        // with the wrong time makes every one of them wrong.
        return "That code was not accepted. Try the newest one; if it keeps failing, check the phone's clock is set automatically.";
      }
      return BAD_CODE;
    case "ExpiredCodeException":
      return "That code has expired. Ask for a new one and use it straight away.";
    default:
      if (message) return message;
      switch (phase) {
        case "new-password":
          return "Could not set the password.";
        case "reset-request":
          return "Could not send a reset code.";
        case "reset-confirm":
          return "Could not set the new password.";
        case "second-step":
          return "Could not finish signing in.";
        case "mfa-setup":
          return "Could not change two-step sign-in.";
        default:
          return "Sign-in failed.";
      }
  }
}

/**
 * True for the exception Cognito raises for an address it has no account for.
 *
 * `requestPasswordReset` swallows exactly this one, so the decision of what it
 * looks like lives here with the rest of the error reading rather than in
 * `auth.ts`.
 */
export function isUserNotFound(err: unknown): boolean {
  return errorName(err) === "UserNotFoundException";
}
